import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Provider } from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { PROVIDERS_SEARCH_LC_KEY } from '../../../constants';
import { InputSearch } from 'src/components/Inputs/InputSearch/InputSearch';
import { RecentSearches } from '../../RecentSearches';
import { NoGamesStub } from '../../NoGamesStub';
import noProviders from 'src/assets/images/noSearchProviders.webp';
import { ProviderButton } from 'src/components/Buttons/ProviderButton';
import { useModalContext } from 'src/hooks/useModalContext';

interface ProvidersSearchResults {
  isFetching: boolean;
  providers: Provider[];
}

export const ProvidersSearchResults: FC<ProvidersSearchResults> = ({ isFetching, providers }) => {
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const { closeModal } = useModalContext();

  useLayoutEffect(() => {
    const dataFromLocalStorage = localStorage.getItem(PROVIDERS_SEARCH_LC_KEY);
    const parsedResult = dataFromLocalStorage && JSON.parse(dataFromLocalStorage);
    const savedRecentSearches = Array.isArray(parsedResult) ? parsedResult : [];

    if (savedRecentSearches.length) {
      setRecentSearches(savedRecentSearches?.slice(-6));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(PROVIDERS_SEARCH_LC_KEY, JSON.stringify(recentSearches));
  }, [recentSearches]);

  const providersToRender = useMemo(
    () =>
      providers?.filter((provider) =>
        provider.name.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [searchValue, providers]
  );

  const addRecentSearchItem = (item: string) => {
    if (providersToRender.length && item.length >= 2) {
      const items = new Set(recentSearches);
      items.add(item);
      setRecentSearches(Array.from(items));
    }
  };

  const handleRemoveItem = (item: string) => {
    const items = new Set(recentSearches);
    items.delete(item);
    setRecentSearches(Array.from(items));
  };

  const handleItemClick = (searchValue: string) => {
    setSearchValue(searchValue);
  };

  const handleInputBlur = () => {
    if (searchValue && providersToRender.length) {
      addRecentSearchItem(searchValue);
    }
  };

  const { t } = useTranslation();
  return (
    <div>
      <InputSearch
        placeholder={`${t('general.searchProvider')}`}
        value={searchValue}
        onBlur={handleInputBlur}
        onChange={setSearchValue}
      />
      {!searchValue && Boolean(recentSearches?.length) && (
        <RecentSearches
          items={recentSearches}
          handleDelete={handleRemoveItem}
          handleClick={handleItemClick}
        />
      )}
      {searchValue && Boolean(providersToRender.length) && (
        <>
          <h3 className="text-sm_search_result_text font-bold text-base mt-3">
            {t('general.searchResults')}
          </h3>
          <div className="mt-2 grid grid-cols-2 gap-2 568:grid-cols-3">
            {providersToRender?.map((provider) => (
              <ProviderButton provider={provider} key={provider.id} onClick={closeModal} />
            ))}
          </div>
        </>
      )}

      {!isFetching && providersToRender.length === 0 && (
        <NoGamesStub image={noProviders}>{t('general.noProvidersFound')}</NoGamesStub>
      )}
    </div>
  );
};
